import { BASE_URL_DYNAMIC } from "../services/api/base-query";

/**
 * Resolves a file URL coming from the API (typically /api/files/{guid}) into
 * an absolute URL pointing at the configured backend host.
 *
 * - Absolute http/https URLs are returned unchanged. Legacy DB rows that
 *   survived Phase 7 still carry absolute hosts; we must not double-prefix.
 * - blob: / data: URLs are returned unchanged. Image-cropper previews and
 *   inline encoded assets must keep working.
 * - Empty/null/undefined returns undefined so callers can fall back to a
 *   placeholder with `?? avatarPlaceholder`.
 * - Anything else (relative paths that aren't /api/files/*) is returned as-is.
 *   Bundler-resolved imports like `../images/foo.png` keep working.
 */
export const resolveFileUrl = (url?: string | null): string | undefined => {
  if (!url) return undefined;
  if (/^(https?:|blob:|data:)/i.test(url)) return url;
  if (url.startsWith("/api/files/")) {
    const base = (BASE_URL_DYNAMIC || "").replace(/\/+$/, "");
    return `${base}${url}`;
  }
  return url;
};

/**
 * True when the resolved URL points at the configured API origin. Used by the
 * AppImage/AppVideo/AppAudio wrappers to decide whether to set
 * `crossOrigin="use-credentials"` so the bpreven.auth HttpOnly cookie is sent
 * on cross-origin media-element requests. We deliberately do NOT set the
 * attribute on third-party absolute URLs (CDNs, YouTube thumbnails, etc.) —
 * those origins typically don't return Access-Control-Allow-Credentials and
 * the request would fail.
 */
export const isApiFileUrl = (resolvedUrl?: string): boolean => {
  if (!resolvedUrl || !BASE_URL_DYNAMIC) return false;
  const base = BASE_URL_DYNAMIC.replace(/\/+$/, "");
  return resolvedUrl.startsWith(`${base}/api/files/`);
};
